import * as v2 from './v2';

/**
 * Eventos, no backend V2.
 *
 * As sete rotas `/api/active-event/*` do Strapi viraram quatro no V2
 * (`active`, `effects`, `last`, `calendar`) — contagem, soma e busca deixaram
 * de ser rota porque os eventos ativos são poucos e já viajam inteiros em
 * `/events/active`: derivar aqui é uma requisição a menos, não uma gambiarra.
 *
 * Todas as funções mantêm o ENVELOPE `{ data: ... }` do Strapi porque a store
 * de eventos faz `response.data` em cada action — trocar o envelope obrigaria
 * a reescrever store e componentes juntos, e a migração evita o "big bang".
 */
export const eventService = {
    /**
     * Busca por efeito. O V2 não tem rota de busca: o filtro roda no cliente,
     * sobre a lista de ativos que já está na mão.
     */
    async findByEffect(effectType, effectText) {
        const eventos = await v2.eventosAtivos();
        const texto = (effectText || '').toLowerCase();
        return {
            data: eventos.filter((evento) => {
                const tipoBate = !effectType || evento.effectType === effectType;
                const textoBate = !texto
                    || (evento.name || '').toLowerCase().includes(texto)
                    || (evento.description || '').toLowerCase().includes(texto);
                return tipoBate && textoBate;
            })
        };
    },

    /** Os eventos ativos — `days` já vem calculado pelo adapter do V2. */
    async getActiveEventsWithRemainingDays() {
        return { data: await v2.eventosAtivos() };
    },

    /**
     * O calendário do mês, agrupado por dia.
     *
     * O V2 devolve uma lista chata `[{id, name, typeEvent, startsAt, endsAt}]`;
     * o `CalendarEvents.vue` consome `response.data.data` como
     * `{ [diaDoMes]: [entrada] }`, e cada entrada precisa de `event.publishedAt`
     * (o filtro herdado do Strapi), `createdAt` (início) e `endDate` (fim) no
     * topo — é isso que o componente lê de fato, então é isso que se monta.
     */
    async getMonthEvents(month, year) {
        const eventos = await v2.calendarioDeEventos(month, year);
        // `new Date(year, month, 0)` é o último dia do mês pedido (month é 1-12).
        const ultimoDia = new Date(year, month, 0).getDate();
        const agrupado = {};

        for (const evento of eventos) {
            const inicio = new Date(evento.startsAt);
            const fim = new Date(evento.endsAt);
            for (let dia = 1; dia <= ultimoDia; dia += 1) {
                const comecoDoDia = new Date(year, month - 1, dia, 0, 0, 0, 0);
                const fimDoDia = new Date(year, month - 1, dia, 23, 59, 59, 999);
                if (inicio <= fimDoDia && fim >= comecoDoDia) {
                    if (!agrupado[dia]) agrupado[dia] = [];
                    agrupado[dia].push({
                        id: evento.id,
                        // `publishedAt` recebe o início porque é o campo que o
                        // componente testa para saber se o evento "existe" —
                        // resquício do draft/publish do Strapi.
                        event: {
                            id: evento.id,
                            name: evento.name,
                            description: evento.description,
                            effectType: evento.effectType,
                            effectNumber: evento.effectNumber,
                            typeEvent: evento.typeEvent,
                            publishedAt: evento.startsAt
                        },
                        createdAt: evento.startsAt,
                        endDate: evento.endsAt
                    });
                }
            }
        }

        return { data: { data: agrupado } };
    },

    /** O último evento (ativo ou o mais recente) — ou `null`, sem evento nunca criado. */
    async getLastActiveEvent() {
        return { data: { event: await v2.ultimoEvento() } };
    },

    /**
     * A soma dos efeitos de um tipo. O V2 devolve todos os tipos de uma vez
     * (zeros incluídos); pinça-se aqui o pedido, no envelope antigo.
     */
    async getSumEffectNumbers(effectType) {
        const efeitos = await v2.efeitosDeEvento();
        return { data: { data: { sum: efeitos[effectType] || 0 } } };
    },

    /** Contagem por tipo, derivada dos ativos — a rota do Strapi não existe mais. */
    async getEventTypeCount() {
        const eventos = await v2.eventosAtivos();
        const contagem = { good: 0, neutral: 0, bad: 0 };
        for (const evento of eventos) {
            if (contagem[evento.typeEvent] !== undefined) {
                contagem[evento.typeEvent] += 1;
            }
        }
        return { data: contagem };
    },

    /** Há evento `noRest` ativo? A soma dos efeitos já diz — sem rota dedicada. */
    async hasNoRestEvent() {
        const efeitos = await v2.efeitosDeEvento();
        return { data: { hasNoRest: (efeitos.noRest || 0) > 0 } };
    }
};
